import { FC } from 'react';
import { t } from 'i18next';
import { useDocuments } from '@views/pages/Patient/components/Documents/hooks';
import { theme } from '@store';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { Upload } from '@mui/icons-material';
import ClearIcon from '@mui/icons-material/Clear';
import {
  DeleteFileModal,
  DocumentsTable,
} from '@views/pages/Patient/components/Documents/components';
import { FilePreview, PatientFileUploadModal } from '@components';
import { Select } from 'ui';
import { InsertDriveFile } from '@mui/icons-material';
import { paths } from '@common/types';

interface DocumentsProps {
  isPatientLocked: boolean;
  isReadOnly: boolean;
  tabPanelInstrumentHeight: number;
  patient: paths[`/patient/{patient_id}`]['get']['responses']['200']['content']['application/json'];
}

export const Documents: FC<DocumentsProps> = ({
  isReadOnly,
  isPatientLocked,
  tabPanelInstrumentHeight,
  patient,
}) => {
  const {
    filters,
    setFilters,
    isInitialFilters,
    handleChangeFilterCategory,
    handleChangeFilterSearch,
    categoryOptions,
    entries,
    handleOpenUploadModal,
    handleCloseUploadModal,
    isUploadModalOpen,
    handleOpenDeleteFileModal,
    handleCloseDeleteFileModal,
    isDeleteFileModalOpen,
    handleDeleteFile,
    isPreviewModalOpen,
    handleOpenPreviewFileModal,
    handleClosePreviewFileModal,
    selectedFile,
    isLoading,
    count,
    isFileDownloading,
    handleDownloadPreviewFile,
    previewFile,
  } = useDocuments(patient.id);

  const columns = [
    {
      id: 'file_name',
      label: t('patient.documents.table.headers.fileName'),
      sortable: true,
      width: 120,
    },
    {
      id: 'size',
      label: t('patient.documents.table.headers.size'),
      sortable: true,
      width: 100,
    },
    {
      id: 'category',
      label: t('patient.documents.table.headers.category'),
      sortable: true,
      width: 110,
    },
    {
      id: 'uploaded_by',
      label: t('patient.documents.table.headers.uploadedBy'),
      sortable: true,
      width: 130,
    },
    {
      id: 'uploaded_on',
      label: t('patient.documents.table.headers.uploadedOn'),
      sortable: true,
      width: 130,
    },
    {
      id: 'action',
      label: '',
      sortable: false,
      width: 100,
    },
  ];

  return (
    <>
      <>
        {entries.length === 0 && isInitialFilters && !isLoading ? (
          <Box
            sx={{
              height: 'calc(100vh - 310px)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <InsertDriveFile
              sx={{ width: 56, height: 56, mb: 2 }}
              color='primary'
            />
            <Typography variant='h6'>
              {t('patient.documents.table.noResults.title')}
            </Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              {t('patient.documents.table.noResults.subTitle')}
            </Typography>
            <Button
              variant='outlined'
              startIcon={<Upload />}
              onClick={handleOpenUploadModal}
              disabled={
                isPatientLocked ||
                isReadOnly ||
                patient.account_status === 'Inactive'
              }
              sx={{ mt: 2 }}
            >
              {t('patient.documents.filters.uploadFile')}
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              backgroundColor: theme.palette.common.white,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                padding: 2,
                justifyContent: 'space-between',
                alignItems: 'center',
                border: '1px solid rgba(0, 0, 0, 0.12)',
                borderBottom: 'none',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  width: '70%',
                }}
              >
                <FormControl
                  sx={{ maxWidth: '350px', width: '45%', minWidth: 150 }}
                >
                  <TextField
                    value={filters.search}
                    onChange={handleChangeFilterSearch}
                    label={t('patient.documents.filters.searchFile')}
                    slotProps={{
                      input: {
                        endAdornment: filters.search ? (
                          <InputAdornment position='end'>
                            <IconButton
                              size='small'
                              aria-label='clear search'
                              onClick={() =>
                                setFilters({ ...filters, search: '' })
                              }
                            >
                              <ClearIcon fontSize='small' />
                            </IconButton>
                          </InputAdornment>
                        ) : null,
                      },
                    }}
                  />
                </FormControl>
                <Select
                  sx={{ maxWidth: '350px', width: '45%', minWidth: 150 }}
                  name='documents-category'
                  size='medium'
                  label={t('patient.documents.filters.category')}
                  value={filters.category}
                  onChange={handleChangeFilterCategory}
                  options={categoryOptions}
                />
              </Box>
              <Button
                variant='outlined'
                startIcon={<Upload />}
                onClick={handleOpenUploadModal}
                disabled={
                  isPatientLocked || patient.account_status === 'Inactive'
                }
              >
                {t('patient.documents.filters.uploadFile')}
              </Button>
            </Box>
            <DocumentsTable
              tabPanelInstrumentHeight={tabPanelInstrumentHeight}
              rows={entries}
              columns={columns}
              filters={filters}
              setFilters={setFilters}
              totalCount={count}
              loading={isLoading}
              disabled={isPatientLocked}
              handleOpenDeleteFileModal={handleOpenDeleteFileModal}
              handleOpenPreviewFileModal={handleOpenPreviewFileModal}
              isReadOnly={isReadOnly}
            />
          </Box>
        )}
      </>
      <PatientFileUploadModal
        isOpen={isUploadModalOpen && !isPatientLocked}
        handleClose={handleCloseUploadModal}
        patient_id={patient.id}
      />
      <DeleteFileModal
        isOpen={isDeleteFileModalOpen && !isPatientLocked}
        handleClose={handleCloseDeleteFileModal}
        confirmAction={handleDeleteFile}
      />
      <FilePreview
        isReadOnly={isReadOnly || isPatientLocked}
        isOpen={isPreviewModalOpen}
        handleClose={handleClosePreviewFileModal}
        fileDetails={selectedFile}
        file={previewFile}
        isFileDownloading={isFileDownloading}
        handleDownload={handleDownloadPreviewFile}
        handleDelete={handleOpenDeleteFileModal}
      />
    </>
  );
};
